// Product-intent detection for the search bar. When a user types anything
// that reads like a physical product our affiliate partners stock (a sofa, a
// mattress, tapware, a BBQ, a brand name, "homewares"…), we surface a "See
// products" tile that routes them into the relevant /products department so
// they can browse and buy direct from the retailer.
//
// Keywords are a hand-curated allowlist of specific product nouns per
// department (NOT auto-derived from labels) so generic words like "home" or
// "office" never hijack a non-shopping query. Matching is word-boundary based.

import { PRODUCTS_DEPARTMENTS, PRODUCTS_BRANDS } from './productsCatalog';

const DEPT_KEYWORDS = {
  'furniture': [
    'sofa', 'couch', 'lounge suite', 'lounge', 'armchair', 'recliner', 'ottoman',
    'coffee table', 'tv unit', 'entertainment unit', 'sideboard', 'buffet',
    'bookshelf', 'bookcase', 'shelving', 'desk', 'office chair', 'dining table',
    'dining chair', 'dining setting', 'bar stool', 'stool', 'bench seat',
    'console table', 'cabinet', 'furniture',
  ],
  'lighting-decor': [
    'floor lamp', 'table lamp', 'lamp', 'pendant light', 'pendant', 'chandelier',
    'ceiling light', 'light fitting', 'lighting', 'rug', 'runner rug', 'mirror',
    'cushion', 'throw', 'vase', 'artwork', 'wall art', 'canvas print', 'curtain',
    'curtains', 'blinds', 'candle', 'diffuser', 'home fragrance', 'homewares',
    'home decor', 'decor',
  ],
  'kitchen-dining': [
    'cookware', 'saucepan', 'frypan', 'frying pan', 'pots and pans', 'knife set',
    'dinner set', 'dinnerware', 'tableware', 'glassware', 'wine glass', 'tumbler',
    'cutlery', 'utensil', 'chopping board', 'bakeware', 'kettle', 'toaster',
    'blender', 'kitchenware',
  ],
  'bedroom-sleep': [
    'mattress', 'bed frame', 'bed base', 'ensemble base', 'headboard', 'bed',
    'pillow', 'quilt', 'doona', 'duvet', 'comforter', 'bedding', 'manchester',
    'bed sheet', 'sheet set', 'bed linen', 'mattress topper', 'wardrobe',
    'tallboy', 'bedside table', 'nightstand',
  ],
  'outdoor-garden': [
    'outdoor furniture', 'outdoor setting', 'outdoor lounge', 'outdoor dining',
    'bbq', 'barbecue', 'outdoor umbrella', 'market umbrella', 'gazebo',
    'planter', 'sun lounge', 'daybed', 'fire pit', 'firepit', 'outdoor heater',
    'patio heater', 'hammock', 'trampoline', 'swing set', 'pressure cleaner',
    'pressure washer', 'lawn mower', 'whipper snipper', 'shade sail',
  ],
  'cleaning-tools': [
    'robot vacuum', 'vacuum cleaner', 'vacuum', 'steam mop', 'mop',
    'cleaning supplies', 'power tool', 'cordless drill', 'drill', 'tool box',
    'toolbox', 'workbench', 'garage storage', 'storage shelving', 'generator',
    'clothes airer', 'ironing board',
  ],
  'bathroom-reno': [
    'vanity', 'vanities', 'basin', 'tapware', 'tap', 'mixer tap', 'mixer',
    'shower screen', 'showerhead', 'shower head', 'shower', 'toilet', 'bidet',
    'bathtub', 'freestanding bath', 'bath', 'tile', 'tiles', 'towel rail',
    'heated towel rail', 'bathroom',
  ],
};

const GENERIC_PRODUCT_WORDS = [
  'products', 'product', 'homewares', 'appliances', 'appliance',
  'where to buy', 'buy online',
];

const DEPT_LABEL = PRODUCTS_DEPARTMENTS.reduce((m, d) => { m[d.key] = d.label; return m; }, {});

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Flatten to { keyword, key } sorted by specificity (longest first) so
// "dining table" wins over "table", "robot vacuum" over "vacuum".
const DEPT_INDEX = Object.entries(DEPT_KEYWORDS)
  .flatMap(([key, kws]) => kws.map((keyword) => ({ keyword, key })))
  .sort((a, b) => b.keyword.length - a.keyword.length);

const hasWord = (q, kw) => new RegExp(`\\b${escapeRe(kw)}\\b`, 'i').test(q);

/**
 * resolveProductSuggestion(query) → { to, label, department, brand? } | null
 * Returns the best product-section route for a shopping-intent query.
 */
export function resolveProductSuggestion(query) {
  const q = String(query || '').toLowerCase().trim();
  if (q.length < 3) return null;

  // 1) Specific department product noun.
  for (const { keyword, key } of DEPT_INDEX) {
    if (hasWord(q, keyword)) {
      const label = DEPT_LABEL[key] || 'products';
      return { to: `/products/${key}`, department: label, label: `Shop ${label}` };
    }
  }

  // 2) Affiliate brand name → the full product range.
  for (const b of PRODUCTS_BRANDS) {
    const name = String(b.name || '').toLowerCase();
    if (name.length >= 4 && q.includes(name)) {
      return { to: '/products', department: b.name, label: `Shop ${b.name} & more`, brand: true };
    }
  }

  // 3) Generic shopping language → the product landing.
  for (const w of GENERIC_PRODUCT_WORDS) {
    if (hasWord(q, w)) {
      return { to: '/products', department: null, label: 'Shop our product range' };
    }
  }

  return null;
}
